import React, { useEffect, useMemo, useRef, useState } from "react"
import Modal from "react-modal"
import { AlertIcon, IconDanger, IconSuccess } from "../ImageComponents"
import { Button, ButtonLoader, ModalConfirmButton } from "../Button"
import { CardModal } from "../Card"
import { UserAvatarInfo } from "../UserList"
import { AiOutlineHistory } from "react-icons/ai"
import { CheckBox } from "../Form"
import { Input, SelectInput } from "../../components/Form"
import { TfiMore } from "react-icons/tfi"
import MessageContainer from "../../container/MessageContainer"
import MessageNewsContainer from "../../container/MessageNewsContainer"
import "./styles.scss"
import { LoadingWithLabel } from "../Loading"
import { AutoSizer, CellMeasurerCache, List, CellMeasurer } from "react-virtualized"
import { ButtonClose } from "../ChatCloseButton"

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
  },
}

const renderIcon = (param) => {
  switch (param) {
    case "success":
      return <IconSuccess />
    case "error":
      return <IconDanger />
    default:
      return <></>
  }
}

const renderMessage = (param) => {
  switch (param) {
    case "success":
      return "Pronto !"
    case "error":
      return "Opa! Encontramos alguma falha"
    default:
      return "Aguarde..."
  }
}

const renderSubMessage = (param, message, defaultLoadingMessage) => {
  switch (param) {
    case "success":
      return message
    case "error":
      return message
    default:
      return defaultLoadingMessage || "Estamos processando as informações"
  }
}

export function ModalPanel({
  children,
  defaultLoadingMessage,
  modalIsOpen,
  closeModal,
  hasButtonClose,
  message,
  alertType,
  width,
  hasModalDown = false,
  shouldCloseOnOverlayClick,
}) {

  const [isScrollable, setIsScrollable] = useState(false);
  const [container, setContainer] = useState(null);
  const containerRef = useRef(container);
  containerRef.current = container;

  const { height } = useWindowDimensions()

  useEffect(() => {
    if(containerRef.current?.clientHeight < height){
      setIsScrollable(true)
    }
    else {
      setIsScrollable(false)
    }
  }, [height, containerRef.current?.clientHeight])

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        onRequestClose={closeModal}
        ariaHideApp={false}
        onAfterOpen={() => {}}
        backdropOpacity={2}
        contentRef={(element) => {
          if (!element) return;
          setContainer(element)
        }}
        className={
          hasModalDown ? "react_portal_custom-trello  react_portal_custom--hasdown" : isScrollable ? "react_portal_custom-new modal_scroll" : "react_portal_custom-new"
        }
        overlayClassName={
          hasModalDown
            ? "react_portal_custom-overlay react_portal_custom-overlay--hasdown"
            : "react_portal_custom-overlay"
        }>
        <div className="modal-panel__button">
          {hasButtonClose && (
           <ButtonClose handleClick={closeModal} />
          )}
        </div>
        <div className="modal-content" style={{ width: width }}>
          {children ? (
            children
          ) : (
            <>
              <div className="modal-panel__content-icon">{renderIcon(alertType)}</div>
              <div className="modal-panel__content-title">
                <h2>{renderMessage(alertType)}</h2>
              </div>
              <div className="modal-panel__content-submessage">
                <h2>{renderSubMessage(alertType, message, defaultLoadingMessage)}</h2>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export function ModalHeader({ title, children, closeModal, buttonCloseDisabled, line = true, hasContent = false }) {
  return (
    <div className={`modal-header ${line ? "" : "no-line"} ${children && hasContent ? "with-content" : ""}`}>
      {title && !children ? <h3 className="modal-title">{title}</h3> : <div className="modal-header__content">{children}</div>}
      {closeModal && (
         <ButtonClose handleClick={closeModal} />
      )}
    </div>
  )
}

export function ModalBody({ children, className, bodyClassName, styles, ref, hideScroll = false }) {
  return (
    <div className={`modal-body${hideScroll ? " hide-scroll" : ""}${bodyClassName ? " " + bodyClassName : ""}`}>
      <div ref={ref} className={`content-modal${className ? " " + className : ""}`} style={styles}>
        {children}
      </div>
    </div>
  )
}

export function ModalDouble({ title, children, position, className, noPadding, noBorderTitle }) {
  return (
    <div
      className={`content-modal--${position}${className ? " " + className : ""}`}
      style={noPadding && { padding: 0 }}>
      {title && (
        <div className={`content-modal__header${noBorderTitle ? " " + "no-border" : ""}`}>
          <h2>{title}</h2>
        </div>
      )}
      {children}
    </div>
  )
}

export function ModalFooter({ children, className, styles, ...props }) {
  return (
    <div {...props} className={`modal-footer${className ? " " + className : ""}`}>
      {children && (
        <div className="modal-footer__content" style={styles}>
          {children}
        </div>
      )}
    </div>
  )
}

export function ModalPanelConfirmAlert({
  modalIsOpen,
  closeModal,
  confirmAction,
  buttonYesLabel,
  buttonNoLabel,
  message,
}) {
  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      ariaHideApp={false}
      onAfterOpen={() => {}}
      style={customStyles}
      backdropOpacity={1}
      className="modal-panel-confirm">
      <div className="modal-panel-confirm__content">
        <div className="modal-panel-confirm__content-title">
          <h2>{message}</h2>
        </div>
        <div className="modal-panel-confirm__content-buttons">
          <div className="confirm-buttons confirm-buttons--no">
            <ModalConfirmButton label={buttonNoLabel} color={"primary"} handleClick={closeModal} />
          </div>
          <div className="confirm-buttons confirm-buttons--yes">
            <ModalConfirmButton
              label={buttonYesLabel}
              color={"danger"}
              handleClick={confirmAction}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export function ModalRemoveItem({
  labelText,
  modalTitle,
  closeModal,
  modalIsOpen,
  handleClick,
  loading,
  hasCancelButton,
}) {
  return (
    <ModalPanel modalIsOpen={modalIsOpen}>
      <ModalHeader closeModal={() => closeModal()} title={modalTitle || "Excluir Item"} />
      <ModalBody className="modal-remove-content">
        <CardModal>
          <div className="modal-remove">
            <AlertIcon />
            <h2>{`Deseja mesmo remover ${labelText || "este item ?"}`}</h2>
          </div>
        </CardModal>
      </ModalBody>
      <ModalFooter className="modal-remove-buttons">
        <ButtonLoader
          loading={loading}
          messageProcess="Removendo"
          label={"Excluir"}
          color="orange"
          handleClick={handleClick}
        />
        <Button label="Cancelar" color="danger" handleClick={() => closeModal()} />
      </ModalFooter>
    </ModalPanel>
  )
}

export function ModalUserList({
  data,
  modalTitle = "Lista de usuários",
  closeModal,
  modalIsOpen,
  changeSort,
  changeCheck,
  checked,
  onSearch,
  onSelectInput,
  onActions,
  onHistory,
  loading,
}) {

  const ref = useRef(null);

  const producers = data.flatMap((item) => item?.producers)

  const cache = useMemo(() => {
    return new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 60
    });
  }, [producers]);

  const RowRender = ({ index, key, parent, style }) => {
    const item = data[index]

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
        <div style={style} key={key}>
          <div style={{marginLeft: "16px", marginTop: "8px", fontSize: "14px", fontWeight: "500"}}>
            <h5>Conteúdo: {item?.post?.title}</h5>
          </div>
          {item?.producers?.map((prod) => {
            return (
              <div style={{margin: "16px 0"}} className={`header-table-modal card-user ${prod?.isFinish ? "" : "warning"}`}>
                <div className="class-check">
                  <CheckBox
                    onChange={(event) => changeCheck(prod, event.target.value)}
                    value={checked && checked.find((row) => row.id === prod?.id) ? false : true}
                    checked={checked && checked.find((row) => row.id === prod.id)}
                  />
                  <UserAvatarInfo
                    row={{
                      ...prod,
                      phone: false,
                    }}
                    padding={false}
                    fixed={false}
                    className={"user-avatar-cell-journey"}
                  />
                </div>
                <div className="div-journey-row">
                  <text className="text-table">{prod?.isFinish ? "Sim" : "Não"}</text>
                  <AiOutlineHistory className="icon-history" onClick={() => onHistory(prod?.id)} />
                </div>
              </div>
            )
          })}
        </div>
      </CellMeasurer>
    )
  }

  return (
    <ModalPanel modalIsOpen={modalIsOpen} width={800}>
      <ModalHeader closeModal={() => closeModal()} title={modalTitle} line={false} />
      <ModalBody className="modal-remove-content list">
        <div className="filter-buttons">
          <div className="input-select">
            <Input
              placeholder={"Pesquise pelo nome ou email"}
              onChange={(value) => onSearch(value)}
              hasIcon={<i class="fi fi-rr-search"></i>}
              hasFocus={false}
            />
            <SelectInput
              isClearable={true}
              placeholder="Selecione a jornada"
              value={{}}
              isMulti={false}
              options={[
                { id: 0, label: "Sim", value: true, name: "fullJourney" },
                { id: 1, label: "Não", value: false, name: "fullJourney" },
              ]}
              onChange={(value) => onSelectInput(value)}
              isOverModal={true}
            />
            <TfiMore
              size={24}
              className={`icon-filter-modal ${checked.length === 0 ? "block" : ""}`}
              onClick={checked.length === 0 ? null : onActions}
            />
          </div>
        </div>
        <div className="header-table-modal">
          <div className="class-check">
            <CheckBox
              onChange={(event) => changeCheck(producers, event.target.value)}
              value={checked.length === producers.length ? false : true}
              checked={checked.length === producers.length && producers.length !== 0}
            />
            <text className="text-table bold pointer" onClick={changeSort}>
              Usuário
            </text>
          </div>
          <text className="text-table bold">Jornada Completa</text>
        </div>
        {data && !data.length && !loading ? (
          <div className="not-data">
            <text>Não há itens a mostrar!</text>
          </div>
        ) : loading ? (
          <div
            style={{
              height: 250,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <LoadingWithLabel colorSpinner="primary" />
          </div>
        ) : (
          <div style={{ height: "250px", width: "97%", overflowY: "auto", overflowX: "hidden" }}>
            <AutoSizer>
              {({ width, height }) => (
                <List
                  ref={ref}
                  autoHeight
                  deferredMeasurementCache={cache}
                  height={height}
                  rowCount={data?.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={RowRender}
                  width={width - 25}
                />
              )}
            </AutoSizer>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button label="Cancelar" color="danger" handleClick={() => closeModal()} />
      </ModalFooter>
    </ModalPanel>
  )
}

export function ModalActions({ closeModal, modalIsOpen, selectedsUsers, defaultPostIds }) {
  const [messageModalIsOpen, setMessageModalIsOpen] = useState(false)
  const [modalNewsIsOpen, setModalNewsIsOpen] = useState(false)

  return (
    <ModalPanel modalIsOpen={modalIsOpen}>
      <ModalHeader closeModal={() => closeModal()} title={`Ações (${selectedsUsers.length})`} />
      <ModalBody className="modal-actions">
        <CardModal>
          <div className="modal-buttons-group">
            <div className="modal-buttons">
              <Button label="Enviar Mensagem" handleClick={() => setMessageModalIsOpen(true)} color="orange"/>
              <Button label="Enviar Conteúdo" handleClick={() => setModalNewsIsOpen(true)} color="orange"/>

              {messageModalIsOpen && (
                <MessageContainer
                  setMessageModalIsOpen={setMessageModalIsOpen}
                  messageModalIsOpen={messageModalIsOpen}
                  selectedsUsers={selectedsUsers.filter((row, idx) => !selectedsUsers.slice(0,idx).find(item => item.id === row.id))}
                />
              )}
              {modalNewsIsOpen && (
                <MessageNewsContainer
                  selectedsUsers={selectedsUsers}
                  defaultPostIds={defaultPostIds}
                  setModalNewsIsOpen={setModalNewsIsOpen}
                  modalNewsIsOpen={modalNewsIsOpen}
                />
              )}
            </div>
          </div>
        </CardModal>
      </ModalBody>
    </ModalPanel>
  )
}
